'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchOrCreateCheckout,
  addLineItemsToCheckout,
  recreateAndAddLineItems,
  updateLineItemQuantity,
  updateLineItemsQuantity,
  removeLineItem as removeLineItemApi,
  updateCheckoutEmail,
  updateCheckoutShippingAddress,
  getCustomerShippingAddress,
  fetchShopInfo,
  fetchAllProducts,
  fetchProduct,
  getVendorClient,
  getDefaultClient,
} from '@/lib/api/shopifyApi';
import { Checkout } from '@/lib/schemas/shopify';

// ---------------------------------------------------------------------------
// Query key factories
// ---------------------------------------------------------------------------
export const shopifyKeys = {
  cart: () => ['shopify', 'cart'] as const,
  guestCart: () => ['shopify', 'guest-cart'] as const,
  shop: () => ['shopify', 'shop'] as const,
  address: (customerId: string) => ['shopify', 'address', customerId] as const,
};

// ---------------------------------------------------------------------------
// Helper: persist cart ID to cookie
// ---------------------------------------------------------------------------
function setCartCookieSafe(cartId: string | null | undefined, cookieName: string) {
  if (typeof window !== 'undefined') {
    (window as any).setCookieWithoutSecure?.(cookieName, cartId ?? null, 365);
  }
}

// ---------------------------------------------------------------------------
// Main hook — replaces `useShopify()` from shopifyReducer.js
// ---------------------------------------------------------------------------

/**
 * Unified hook for all Shopify cart operations.
 *
 * Replaces: `useShopify`, `shopifyReducer.js`, every `createAsyncThunk` inside it.
 *
 * The cart is stored in React Query's cache under the key `['shopify', 'cart']`.
 * All three consumers (AddToCartIsland, ShopifyCart, LineItem) share this same key
 * — no prop drilling, no Redux store required.
 */
export function useShopifyCart() {
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Cart (logged-in user)
  // -------------------------------------------------------------------------
  const cartQuery = useQuery<Checkout | null, Error>({
    queryKey: shopifyKeys.cart(),
    queryFn: () => null,          // We never auto-fetch; callers trigger `createCheckout`
    staleTime: Infinity,          // Cart is source-of-truth until explicitly invalidated
    enabled: false,               // Only populated via mutations / manual setQueryData
  });

  const checkoutState: Checkout | null = cartQuery.data ?? null;

  // -------------------------------------------------------------------------
  // Guest cart
  // -------------------------------------------------------------------------
  const guestCartQuery = useQuery<Checkout | null, Error>({
    queryKey: shopifyKeys.guestCart(),
    queryFn: () => null,
    staleTime: Infinity,
    enabled: false,
  });

  const guestCheckoutState: Checkout | null = guestCartQuery.data ?? null;

  // -------------------------------------------------------------------------
  // Derived cart state
  // -------------------------------------------------------------------------
  const cartQuantity: number =
    checkoutState?.lineItems?.reduce((acc, item) => acc + (item.quantity ?? 0), 0) ?? 0;

  const cartCount: number = cartQuantity;

  const skusCommaSeperated: (string | undefined)[] | undefined =
    checkoutState?.lineItems?.map((li) => li.variant?.sku);

  const checkoutID: string | undefined = checkoutState?.id;

  // -------------------------------------------------------------------------
  // Mutation: create / load checkout
  // -------------------------------------------------------------------------
  const createCheckoutMutation = useMutation<
    Checkout,
    Error,
    { checkoutId?: string | null; guest?: boolean }
  >({
    mutationFn: ({ checkoutId }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return fetchOrCreateCheckout(checkoutId).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data, variables) => {
      if (variables.guest) {
        queryClient.setQueryData<Checkout | null>(shopifyKeys.guestCart(), data);
      } else {
        queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
      }
    },
  });

  const createCheckout = (config: { checkoutId?: string | null; guest?: boolean }) =>
    createCheckoutMutation.mutateAsync(config);

  // -------------------------------------------------------------------------
  // Mutation: add variant(s) to cart
  // -------------------------------------------------------------------------
  const addVariantMutation = useMutation<
    Checkout,
    Error,
    {
      checkoutId: string;
      lineItemsToAdd: { variantId: string; quantity: number; customAttributes?: { key: string; value: string }[] }[];
      successCallback?: (success: boolean) => void;
    }
  >({
    mutationFn: ({ checkoutId, lineItemsToAdd }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return addLineItemsToCheckout(checkoutId, lineItemsToAdd).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
      variables.successCallback?.(true);
    },
  });

  const addVariant = (
    checkoutId: string,
    lineItemsToAdd: { variantId: string; quantity: number; customAttributes?: { key: string; value: string }[] }[],
    successCallback?: (success: boolean) => void,
  ) => addVariantMutation.mutateAsync({ checkoutId, lineItemsToAdd, successCallback });

  // -------------------------------------------------------------------------
  // Mutation: recreate checkout and add variants (replaces stale cart)
  // -------------------------------------------------------------------------
  const recreateCheckoutMutation = useMutation<
    Checkout,
    Error,
    {
      checkoutId?: string | null;
      lineItemsToAdd: { variantId: string; quantity: number }[];
      successCallback?: (success: boolean) => void;
      onDBUpdate?: (checkout: Checkout) => void;
    }
  >({
    mutationFn: ({ lineItemsToAdd }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return recreateAndAddLineItems(lineItemsToAdd).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
      // Trigger DB update callback (replaces the updateDB Redux flag)
      variables.onDBUpdate?.(data);
      variables.successCallback?.(true);
    },
  });

  const createNewCheckOutAndaddVariant = (
    checkoutId: string | null,
    lineItemsToAdd: { variantId: string; quantity: number }[],
    successCallback?: (success: boolean) => void,
    onDBUpdate?: (checkout: Checkout) => void,
  ) => recreateCheckoutMutation.mutate({ checkoutId, lineItemsToAdd, successCallback, onDBUpdate });

  // -------------------------------------------------------------------------
  // Mutation: update single quantity
  // -------------------------------------------------------------------------
  const updateQuantityMutation = useMutation<
    Checkout,
    Error,
    { lineItemId: string; quantity: number; checkoutID: string }
  >({
    mutationFn: ({ lineItemId, quantity, checkoutID }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return updateLineItemQuantity(checkoutID, lineItemId, quantity).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
    },
  });

  const updateQuantity = (lineItemId: string, quantity: number, checkoutID: string) =>
    updateQuantityMutation.mutate({ lineItemId, quantity, checkoutID });

  // -------------------------------------------------------------------------
  // Mutation: update multiple quantities
  // -------------------------------------------------------------------------
  const updateQuantitiesMutation = useMutation<
    Checkout,
    Error,
    { lineItemsToUpdate: { id: string; quantity: number }[]; checkoutID: string }
  >({
    mutationFn: ({ lineItemsToUpdate, checkoutID }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return updateLineItemsQuantity(checkoutID, lineItemsToUpdate).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
    },
  });

  const updateQuantities = (
    lineItemsToUpdate: { id: string; quantity: number }[],
    checkoutID: string,
  ) => updateQuantitiesMutation.mutate({ lineItemsToUpdate, checkoutID });

  // -------------------------------------------------------------------------
  // Mutation: remove line item
  // -------------------------------------------------------------------------
  const removeLineItemMutation = useMutation<
    Checkout,
    Error,
    { checkoutID: string; lineItemId: string }
  >({
    mutationFn: ({ checkoutID, lineItemId }) => {
      if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = true;
      return removeLineItemApi(checkoutID, lineItemId).finally(() => {
        if (typeof window !== 'undefined') (window as any).nestingale.apiTriggered = false;
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
    },
  });

  const removeLineItem = (checkoutID: string, lineItemId: string) =>
    removeLineItemMutation.mutate({ checkoutID, lineItemId });

  // -------------------------------------------------------------------------
  // Mutation: update email in checkout
  // -------------------------------------------------------------------------
  const updateEmailMutation = useMutation<
    Checkout | undefined,
    Error,
    { checkoutId: string; emamilAddress?: string }
  >({
    mutationFn: ({ checkoutId, emamilAddress }) => updateCheckoutEmail(checkoutId, emamilAddress),
    onSuccess: (data) => {
      if (data) queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
    },
  });

  const saveEmailAddressInShopify = (postData: { checkoutId: string; emamilAddress?: string }) =>
    updateEmailMutation.mutate(postData);

  // -------------------------------------------------------------------------
  // Mutation: update shipping address
  // -------------------------------------------------------------------------
  const updateShippingMutation = useMutation<
    Checkout | undefined,
    Error,
    { checkoutId: string; shippingAddress: { addressList?: any[] } }
  >({
    mutationFn: ({ checkoutId, shippingAddress }) =>
      updateCheckoutShippingAddress(checkoutId, shippingAddress),
    onSuccess: (data) => {
      if (data) queryClient.setQueryData<Checkout | null>(shopifyKeys.cart(), data);
    },
  });

  const saveShippingAddressInShopify = (postData: {
    checkoutId: string;
    shippingAddress: { addressList?: any[] };
  }) => updateShippingMutation.mutate(postData);

  // -------------------------------------------------------------------------
  // Query: shipping address from Nestingale API
  // -------------------------------------------------------------------------
  const dbAddressQuery = useQuery({
    queryKey: shopifyKeys.address(''),
    queryFn: () => null,
    staleTime: Infinity,
    enabled: false,
  });

  const dbAddressInfo = dbAddressQuery.data ?? null;

  const fetchShippingAddressInShopify = async (postData: {
    customerId: string;
    brand?: string;
  }) => {
    const data = await getCustomerShippingAddress(postData.customerId, postData.brand);
    queryClient.setQueryData(shopifyKeys.address(postData.customerId), data);
  };

  // -------------------------------------------------------------------------
  // Misc helpers — rarely used, kept for backward compat
  // -------------------------------------------------------------------------
  const setCount = (_count: number) => {
    // cartCount is now derived from checkout.lineItems — no explicit setter needed.
    // This is a no-op stub kept so existing callers don't break.
  };

  const fetchProducts = () => {
    // fetchProducts was used to refresh product list from Redux; now a no-op.
    // Consumers should call getProductsByIds directly if needed.
  };

  const getClient = () => getDefaultClient();

  // -------------------------------------------------------------------------
  // Return shape mirrors the old `useShopify()` for drop-in replacement
  // -------------------------------------------------------------------------
  return {
    // State
    checkoutState,
    guestCheckoutState,
    cartCount,
    cartQuantity,
    skusCommaSeperated,
    checkoutID,
    dbAddressInfo,
    cartStatus: true,          // was always `true` in the old hook
    createNewCart: false,      // no longer needed — handled internally in mutations
    updateDB: false,           // no longer needed — handled in onSuccess callbacks
    shopDetails: null,         // use useShopInfo() hook if needed

    // Checkout operations
    createCheckout,
    addVariant,
    createNewCheckOutAndaddVariant,
    updateQuantity,
    updateQuantities,
    removeLineItem,
    saveEmailAddressInShopify,
    saveShippingAddressInShopify,
    fetchShippingAddressInShopify,

    // Misc / compat
    getClient,
    getVendorClient,
    fetchProducts,
    setCount,
  };
}
